import React, { MouseEvent, ChangeEvent, KeyboardEvent } from 'react';
import debounce from 'lodash/debounce';

interface StateType {
  value: string;
  loading: boolean;
  options: any[];
  error?: any;
}

type ActionType =
  | { type: 'VALUE_CHANGED'; payload: { value: string } }
  | { type: 'DATA_RETRIEVED'; payload: { options: any[] } }
  | { type: 'ERROR'; payload: { error?: any } };

const reducer = (state: StateType, action: ActionType): StateType => {
  switch (action.type) {
    case 'VALUE_CHANGED':
      return {
        ...state,
        value: action.payload.value,
        loading: true,
        error: undefined,
      };
    case 'DATA_RETRIEVED':
      return {
        ...state,
        options: action.payload.options,
        loading: false,
        error: undefined,
      };
    case 'ERROR':
      return {
        ...state,
        options: [] as any[],
        loading: false,
        error: action.payload.error,
      };
    default:
      throw new Error();
  }
};

const debounced = debounce(callback => callback(), 200);

const initialState: StateType = {
  value: '',
  loading: false,
  options: [] as any[],
};

export interface UseAsyncTypeaheadOptions {
  request: (
    e: MouseEvent<HTMLButtonElement> | ChangeEvent<HTMLInputElement> | KeyboardEvent<HTMLInputElement>,
    { value }: { value: string },
  ) => Promise<any[]>;
}

const useAsyncTypeahead = ({ request }: UseAsyncTypeaheadOptions) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);

  const onChange = React.useCallback(
    (e: MouseEvent<HTMLButtonElement> | ChangeEvent<HTMLInputElement> | KeyboardEvent<HTMLInputElement>, { value }) => {
      dispatch({
        type: 'VALUE_CHANGED',
        payload: { value },
      });
      debounced(() => {
        request(e, { value })
          .then(opts => {
            dispatch({
              type: 'DATA_RETRIEVED',
              payload: { options: opts },
            });
          })
          .catch(e => {
            dispatch({ type: 'ERROR', payload: { error: e } });
            console.warn(e);
          });
      });
    },
    [request],
  );

  return {
    ...state,
    onChange,
  };
};

export default useAsyncTypeahead;
